// Platform brand colors as HSL values for use in dynamic theming
export interface PlatformTheme {
  primary: string;
  primaryLight: string;
  glow: string;
  iconFilter: string;
  gradient: string;
  textShadow: string;
  borderColor: string;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  buttonBg: string;
  buttonBorder: string;
  buttonHoverBg: string;
  buttonHoverShadow: string;
  cardHoverBorder: string;
  accentGradient: string;
}

const themes: Record<string, PlatformTheme> = {
  instagram: {
    primary: "hsl(330, 80%, 55%)",
    primaryLight: "hsl(330, 80%, 55%)",
    glow: "hsla(330, 80%, 55%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(42%) sepia(80%) saturate(2000%) hue-rotate(300deg) brightness(100%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(45, 100%, 55%), hsl(330, 80%, 55%), hsl(270, 80%, 55%))",
    textShadow: "0 0 20px hsla(330, 80%, 55%, 0.6)",
    borderColor: "hsla(330, 80%, 55%, 0.4)",
    badgeBg: "hsla(330, 80%, 55%, 0.1)",
    badgeText: "hsl(330, 80%, 55%)",
    badgeBorder: "hsla(330, 80%, 55%, 0.2)",
    buttonBg: "hsla(330, 80%, 55%, 0.1)",
    buttonBorder: "hsla(330, 80%, 55%, 0.4)",
    buttonHoverBg: "hsla(330, 80%, 55%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(330, 80%, 55%, 0.25)",
    cardHoverBorder: "hsla(330, 80%, 55%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(45, 100%, 55%, 0.05), hsla(330, 80%, 55%, 0.08), hsla(270, 80%, 55%, 0.05))",
  },
  youtube: {
    primary: "hsl(0, 85%, 50%)",
    primaryLight: "hsl(0, 85%, 55%)",
    glow: "hsla(0, 85%, 50%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(18%) sepia(95%) saturate(6000%) hue-rotate(358deg) brightness(100%) contrast(110%)",
    gradient: "linear-gradient(135deg, hsl(0, 85%, 50%), hsl(0, 70%, 40%))",
    textShadow: "0 0 20px hsla(0, 85%, 50%, 0.6)",
    borderColor: "hsla(0, 85%, 50%, 0.4)",
    badgeBg: "hsla(0, 85%, 50%, 0.1)",
    badgeText: "hsl(0, 85%, 55%)",
    badgeBorder: "hsla(0, 85%, 50%, 0.2)",
    buttonBg: "hsla(0, 85%, 50%, 0.1)",
    buttonBorder: "hsla(0, 85%, 50%, 0.4)",
    buttonHoverBg: "hsla(0, 85%, 50%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(0, 85%, 50%, 0.25)",
    cardHoverBorder: "hsla(0, 85%, 50%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(0, 85%, 50%, 0.05), hsla(0, 70%, 40%, 0.08))",
  },
  tiktok: {
    primary: "hsl(170, 100%, 50%)",
    primaryLight: "hsl(170, 100%, 55%)",
    glow: "hsla(170, 100%, 50%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(75%) sepia(50%) saturate(1500%) hue-rotate(130deg) brightness(105%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(170, 100%, 50%), hsl(340, 80%, 55%))",
    textShadow: "0 0 20px hsla(170, 100%, 50%, 0.6)",
    borderColor: "hsla(170, 100%, 50%, 0.4)",
    badgeBg: "hsla(170, 100%, 50%, 0.1)",
    badgeText: "hsl(170, 100%, 50%)",
    badgeBorder: "hsla(170, 100%, 50%, 0.2)",
    buttonBg: "hsla(170, 100%, 50%, 0.1)",
    buttonBorder: "hsla(170, 100%, 50%, 0.4)",
    buttonHoverBg: "hsla(170, 100%, 50%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(170, 100%, 50%, 0.25)",
    cardHoverBorder: "hsla(170, 100%, 50%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(170, 100%, 50%, 0.05), hsla(340, 80%, 55%, 0.05))",
  },
  twitter: {
    primary: "hsl(203, 89%, 53%)",
    primaryLight: "hsl(203, 89%, 58%)",
    glow: "hsla(203, 89%, 53%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(55%) sepia(70%) saturate(2000%) hue-rotate(180deg) brightness(100%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(203, 89%, 53%), hsl(203, 70%, 40%))",
    textShadow: "0 0 20px hsla(203, 89%, 53%, 0.6)",
    borderColor: "hsla(203, 89%, 53%, 0.4)",
    badgeBg: "hsla(203, 89%, 53%, 0.1)",
    badgeText: "hsl(203, 89%, 58%)",
    badgeBorder: "hsla(203, 89%, 53%, 0.2)",
    buttonBg: "hsla(203, 89%, 53%, 0.1)",
    buttonBorder: "hsla(203, 89%, 53%, 0.4)",
    buttonHoverBg: "hsla(203, 89%, 53%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(203, 89%, 53%, 0.25)",
    cardHoverBorder: "hsla(203, 89%, 53%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(203, 89%, 53%, 0.05), hsla(203, 70%, 40%, 0.08))",
  },
  facebook: {
    primary: "hsl(220, 46%, 48%)",
    primaryLight: "hsl(220, 60%, 55%)",
    glow: "hsla(220, 60%, 55%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(30%) sepia(80%) saturate(1200%) hue-rotate(200deg) brightness(95%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(220, 60%, 55%), hsl(220, 46%, 40%))",
    textShadow: "0 0 20px hsla(220, 60%, 55%, 0.6)",
    borderColor: "hsla(220, 60%, 55%, 0.4)",
    badgeBg: "hsla(220, 60%, 55%, 0.1)",
    badgeText: "hsl(220, 60%, 60%)",
    badgeBorder: "hsla(220, 60%, 55%, 0.2)",
    buttonBg: "hsla(220, 60%, 55%, 0.1)",
    buttonBorder: "hsla(220, 60%, 55%, 0.4)",
    buttonHoverBg: "hsla(220, 60%, 55%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(220, 60%, 55%, 0.25)",
    cardHoverBorder: "hsla(220, 60%, 55%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(220, 60%, 55%, 0.05), hsla(220, 46%, 40%, 0.08))",
  },
  telegram: {
    primary: "hsl(200, 80%, 50%)",
    primaryLight: "hsl(200, 80%, 55%)",
    glow: "hsla(200, 80%, 50%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(55%) sepia(60%) saturate(2000%) hue-rotate(170deg) brightness(100%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(200, 80%, 55%), hsl(200, 70%, 40%))",
    textShadow: "0 0 20px hsla(200, 80%, 55%, 0.6)",
    borderColor: "hsla(200, 80%, 50%, 0.4)",
    badgeBg: "hsla(200, 80%, 50%, 0.1)",
    badgeText: "hsl(200, 80%, 55%)",
    badgeBorder: "hsla(200, 80%, 50%, 0.2)",
    buttonBg: "hsla(200, 80%, 50%, 0.1)",
    buttonBorder: "hsla(200, 80%, 50%, 0.4)",
    buttonHoverBg: "hsla(200, 80%, 50%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(200, 80%, 50%, 0.25)",
    cardHoverBorder: "hsla(200, 80%, 50%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(200, 80%, 55%, 0.05), hsla(200, 70%, 40%, 0.08))",
  },
  spotify: {
    primary: "hsl(141, 73%, 42%)",
    primaryLight: "hsl(141, 73%, 48%)",
    glow: "hsla(141, 73%, 42%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(50%) sepia(70%) saturate(1500%) hue-rotate(100deg) brightness(95%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(141, 73%, 48%), hsl(141, 60%, 30%))",
    textShadow: "0 0 20px hsla(141, 73%, 42%, 0.6)",
    borderColor: "hsla(141, 73%, 42%, 0.4)",
    badgeBg: "hsla(141, 73%, 42%, 0.1)",
    badgeText: "hsl(141, 73%, 48%)",
    badgeBorder: "hsla(141, 73%, 42%, 0.2)",
    buttonBg: "hsla(141, 73%, 42%, 0.1)",
    buttonBorder: "hsla(141, 73%, 42%, 0.4)",
    buttonHoverBg: "hsla(141, 73%, 42%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(141, 73%, 42%, 0.25)",
    cardHoverBorder: "hsla(141, 73%, 42%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(141, 73%, 48%, 0.05), hsla(141, 60%, 30%, 0.08))",
  },
  linkedin: {
    primary: "hsl(210, 80%, 45%)",
    primaryLight: "hsl(210, 80%, 52%)",
    glow: "hsla(210, 80%, 45%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(35%) sepia(80%) saturate(1500%) hue-rotate(190deg) brightness(95%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(210, 80%, 52%), hsl(210, 60%, 35%))",
    textShadow: "0 0 20px hsla(210, 80%, 45%, 0.6)",
    borderColor: "hsla(210, 80%, 45%, 0.4)",
    badgeBg: "hsla(210, 80%, 45%, 0.1)",
    badgeText: "hsl(210, 80%, 52%)",
    badgeBorder: "hsla(210, 80%, 45%, 0.2)",
    buttonBg: "hsla(210, 80%, 45%, 0.1)",
    buttonBorder: "hsla(210, 80%, 45%, 0.4)",
    buttonHoverBg: "hsla(210, 80%, 45%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(210, 80%, 45%, 0.25)",
    cardHoverBorder: "hsla(210, 80%, 45%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(210, 80%, 52%, 0.05), hsla(210, 60%, 35%, 0.08))",
  },
  twitch: {
    primary: "hsl(264, 100%, 64%)",
    primaryLight: "hsl(264, 100%, 70%)",
    glow: "hsla(264, 100%, 64%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(40%) sepia(90%) saturate(3000%) hue-rotate(245deg) brightness(105%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(264, 100%, 70%), hsl(264, 80%, 50%))",
    textShadow: "0 0 20px hsla(264, 100%, 64%, 0.6)",
    borderColor: "hsla(264, 100%, 64%, 0.4)",
    badgeBg: "hsla(264, 100%, 64%, 0.1)",
    badgeText: "hsl(264, 100%, 70%)",
    badgeBorder: "hsla(264, 100%, 64%, 0.2)",
    buttonBg: "hsla(264, 100%, 64%, 0.1)",
    buttonBorder: "hsla(264, 100%, 64%, 0.4)",
    buttonHoverBg: "hsla(264, 100%, 64%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(264, 100%, 64%, 0.25)",
    cardHoverBorder: "hsla(264, 100%, 64%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(264, 100%, 70%, 0.05), hsla(264, 80%, 50%, 0.08))",
  },
  discord: {
    primary: "hsl(235, 86%, 65%)",
    primaryLight: "hsl(235, 86%, 70%)",
    glow: "hsla(235, 86%, 65%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(38%) sepia(80%) saturate(2500%) hue-rotate(220deg) brightness(105%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(235, 86%, 70%), hsl(235, 70%, 50%))",
    textShadow: "0 0 20px hsla(235, 86%, 65%, 0.6)",
    borderColor: "hsla(235, 86%, 65%, 0.4)",
    badgeBg: "hsla(235, 86%, 65%, 0.1)",
    badgeText: "hsl(235, 86%, 70%)",
    badgeBorder: "hsla(235, 86%, 65%, 0.2)",
    buttonBg: "hsla(235, 86%, 65%, 0.1)",
    buttonBorder: "hsla(235, 86%, 65%, 0.4)",
    buttonHoverBg: "hsla(235, 86%, 65%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(235, 86%, 65%, 0.25)",
    cardHoverBorder: "hsla(235, 86%, 65%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(235, 86%, 70%, 0.05), hsla(235, 70%, 50%, 0.08))",
  },
  soundcloud: {
    primary: "hsl(18, 100%, 50%)",
    primaryLight: "hsl(18, 100%, 55%)",
    glow: "hsla(18, 100%, 50%, 0.4)",
    iconFilter: "brightness(0) saturate(100%) invert(45%) sepia(90%) saturate(3000%) hue-rotate(5deg) brightness(105%) contrast(95%)",
    gradient: "linear-gradient(135deg, hsl(18, 100%, 55%), hsl(18, 80%, 40%))",
    textShadow: "0 0 20px hsla(18, 100%, 50%, 0.6)",
    borderColor: "hsla(18, 100%, 50%, 0.4)",
    badgeBg: "hsla(18, 100%, 50%, 0.1)",
    badgeText: "hsl(18, 100%, 55%)",
    badgeBorder: "hsla(18, 100%, 50%, 0.2)",
    buttonBg: "hsla(18, 100%, 50%, 0.1)",
    buttonBorder: "hsla(18, 100%, 50%, 0.4)",
    buttonHoverBg: "hsla(18, 100%, 50%, 0.2)",
    buttonHoverShadow: "0 0 20px hsla(18, 100%, 50%, 0.25)",
    cardHoverBorder: "hsla(18, 100%, 50%, 0.5)",
    accentGradient: "linear-gradient(135deg, hsla(18, 100%, 55%, 0.05), hsla(18, 80%, 40%, 0.08))",
  },
};

const defaultTheme: PlatformTheme = {
  primary: "hsl(174, 90%, 50%)",
  primaryLight: "hsl(174, 90%, 55%)",
  glow: "hsla(174, 90%, 50%, 0.4)",
  iconFilter: "brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(1500%) hue-rotate(130deg) brightness(100%) contrast(95%)",
  gradient: "linear-gradient(135deg, hsl(174, 90%, 50%), hsl(270, 80%, 60%))",
  textShadow: "0 0 20px hsla(174, 90%, 50%, 0.6)",
  borderColor: "hsla(174, 90%, 50%, 0.4)",
  badgeBg: "hsla(174, 90%, 50%, 0.1)",
  badgeText: "hsl(174, 90%, 50%)",
  badgeBorder: "hsla(174, 90%, 50%, 0.2)",
  buttonBg: "hsla(174, 90%, 50%, 0.1)",
  buttonBorder: "hsla(174, 90%, 50%, 0.4)",
  buttonHoverBg: "hsla(174, 90%, 50%, 0.2)",
  buttonHoverShadow: "0 0 20px hsla(174, 90%, 50%, 0.25)",
  cardHoverBorder: "hsla(174, 90%, 50%, 0.5)",
  accentGradient: "linear-gradient(135deg, hsla(174, 90%, 50%, 0.05), hsla(270, 80%, 60%, 0.05))",
};

export function getPlatformTheme(slug: string): PlatformTheme {
  return themes[slug] || defaultTheme;
}
